import React, { useEffect, useState } from 'react';
import { X, Mail, Lock, Loader2, AlertCircle, LogIn, UserPlus, ArrowLeft } from 'lucide-react';
import { signInRetailer, resetPassword, RetailerUser } from '../services/authService';

interface RetailerLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (retailer: RetailerUser) => void;
  onApplyClick: () => void;
}

const RetailerLoginModal: React.FC<RetailerLoginModalProps> = ({
  isOpen,
  onClose,
  onLoginSuccess,
  onApplyClick,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const retailer = await signInRetailer(email, password);
      onLoginSuccess(retailer);
      onClose();
    } catch (err: any) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else if (err.code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError(err.message || 'Failed to sign in.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err: any) {
      setError('Failed to send reset email. Please check your email address.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = () => {
    onClose();
    onApplyClick();
  };

  const inputClass =
    'w-full pl-10 pr-4 py-3 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm transition-opacity duration-300 ease-editorial"
        onClick={onClose}
      />

      <div className="relative bg-paper-bright shadow-float w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-300 ease-editorial">

        {/* Header */}
        <div className="bg-forge text-paper px-6 py-7 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>

          <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-3">
            — Retailer access
          </p>
          <h2 className="font-editorial italic font-light text-3xl text-paper-warm leading-tight tracking-tight">
            {showForgotPassword ? 'Reset password.' : 'Welcome back.'}
          </h2>
          <p className="text-paper/65 text-sm mt-2">
            {showForgotPassword
              ? 'We will send you instructions to reset your password.'
              : 'Sign in to view wholesale pricing.'}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 lg:p-8">

          {error && (
            <div className="mb-5 bg-ember-soft/25 border-l-2 border-ember p-3 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-ember-deep text-sm">{error}</p>
            </div>
          )}

          {showForgotPassword ? (
            resetSent ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 bg-olive/15 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Mail className="w-6 h-6 text-olive" strokeWidth={1.5} />
                </div>
                <h3 className="font-editorial italic text-xl text-ink-950 mb-2">Check your email.</h3>
                <p className="text-sm text-ink-700 mb-8 max-w-prose mx-auto">
                  Password reset instructions sent to{' '}
                  <span className="text-ink-950 font-medium">{email}</span>.
                </p>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetSent(false);
                  }}
                  className="inline-flex items-center gap-2 text-sm text-ember hover:text-ember-deep transition-colors duration-150"
                >
                  <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div>
                  <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" strokeWidth={1.5} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputClass}
                      placeholder="you@business.com"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 bg-ember hover:bg-ember-deep disabled:opacity-50 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                      Sending...
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full text-sm text-ink-700 hover:text-ember transition-colors duration-150 inline-flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Back to sign in
                </button>
              </form>
            )
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" strokeWidth={1.5} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="you@business.com"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" strokeWidth={1.5} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                    placeholder="Enter your password"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-ember hover:text-ember-deep transition-colors duration-150"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-ember hover:bg-ember-deep disabled:opacity-50 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" strokeWidth={1.5} />
                    Sign in
                  </>
                )}
              </button>
            </form>
          )}

          {!showForgotPassword && (
            <div className="mt-8 pt-6 border-t border-ink-200 text-center">
              <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-3">
                — No account yet
              </p>
              <button
                onClick={handleApply}
                className="inline-flex items-center gap-2 text-ember hover:text-ember-deep font-medium text-sm transition-colors duration-150"
              >
                <UserPlus className="w-4 h-4" strokeWidth={1.5} />
                Apply for a wholesale account
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetailerLoginModal;
